// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import { useTheme } from "@mui/material/styles";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import {
  ProgressionExamSummaryType,
  SelectedProgressionExamType,
} from "src/services/exams/types";

// Utils
import { getLocalDate } from "src/utils/date/dateHelper";
import { getVisualField } from "src/utils/exams/examReportsHelper";
import { getExternalExamStats } from "src/utils/exams/progressionAnalysisHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type ExternalExamAnalysisCardType = {
  exam: ProgressionExamSummaryType;
  // Measurements arrive only once the card is selected and its detail is
  // fetched, matching how the RetinaLogik card sources its own stats.
  selectedExam: SelectedProgressionExamType | undefined;
  isSelected: boolean;
  isDisabled: boolean;
  handleExternalExamSelect: (exam: ProgressionExamSummaryType) => Promise<void>;
};

const ExternalExamAnalysisCard = ({
  exam,
  selectedExam,
  isSelected,
  isDisabled,
  handleExternalExamSelect,
}: ExternalExamAnalysisCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const theme = useTheme();
  const { elevatedSurface: unselectedCardBackground } = getSurfaceTokens(theme);

  const examTitle = useMemo(() => exam.algorithm ?? "", [exam.algorithm]);

  const gridType = useMemo(
    () => getVisualField(exam.gridType),
    [exam.gridType]
  );

  const completionDate = useMemo(() => {
    if (exam.completionDate) {
      return getLocalDate(exam.completionDate, clinicInfo?.timezone);
    }
    return "N/A";
  }, [exam.completionDate, clinicInfo?.timezone]);

  // VRFI holds the external exam's VFI
  const stats = useMemo(
    () => getExternalExamStats(selectedExam?.data),
    [selectedExam?.data]
  );

  return (
    <Box
      data-testid={`select-analysis-card-${exam.id}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: "420px",
        borderRadius: "8px",
        backgroundColor: isSelected ? "blue" : unselectedCardBackground,
        padding: "16px",
        gap: "16px",
        cursor: isDisabled ? "not-allowed" : "pointer",
      }}
      onClick={() => {
        if (!isDisabled) handleExternalExamSelect(exam);
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <Checkbox
            disabled={isDisabled}
            checked={isSelected}
            sx={{
              padding: 0,
              color: "#FFF",
              "&.Mui-checked": { color: "#FFF" },
              "&.Mui-disabled": { color: "#FFF" },
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#FFF" }}
            >
              {t(examTitle)} | {gridType}
            </Typography>
            <Box sx={{ display: "flex", gap: "16px" }}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                {completionDate}
              </Typography>{" "}
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("Age")}:</span>{" "}
                {exam.patientAge}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: "8px",
            backgroundColor: "#FFF",
            borderRadius: "2px",
          }}
        >
          <Typography
            sx={{
              color: "blue",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            {t("External")}
          </Typography>
        </Box>
      </Box>

      {isSelected && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Grid container>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("FL")}:</span> {stats.FL}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("FN")}:</span> {stats.FN}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("FP")}:</span> {stats.FP}
              </Typography>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("PSD")}:</span> {stats.PSD}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("MD")}:</span> {stats.MD}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography sx={{ fontSize: "14px", color: "#FFF" }}>
                <span style={{ fontWeight: 600 }}>{t("VFI")}:</span>{" "}
                {stats.VRFI}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default ExternalExamAnalysisCard;
